import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Download, BarChart3, FileSpreadsheet, TrendingUp, X,
  Shield, AlertTriangle, CheckCircle2, ChevronRight, Building2, Loader2, User
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const RISK_COLORS = {
  critical: { bg: 'bg-red-50 border-red-200', text: 'text-red-700', icon: 'text-red-500', badge: 'bg-red-100 text-red-700' },
  high:     { bg: 'bg-orange-50 border-orange-200', text: 'text-orange-700', icon: 'text-orange-500', badge: 'bg-orange-100 text-orange-700' },
  medium:   { bg: 'bg-yellow-50 border-yellow-200', text: 'text-yellow-700', icon: 'text-yellow-500', badge: 'bg-yellow-100 text-yellow-700' },
  low:      { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-700', icon: 'text-blue-500', badge: 'bg-blue-100 text-blue-700' },
  clear:    { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700', icon: 'text-emerald-500', badge: 'bg-emerald-100 text-emerald-700' },
};

const getRiskStyle = (level) => RISK_COLORS[level] || { bg: 'bg-slate-50 border-slate-200', text: 'text-slate-700', icon: 'text-slate-400', badge: 'bg-slate-100 text-slate-700' };

export default function UsageExportPage({ user, onLogout }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedOrg, setSelectedOrg] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [riskDetail, setRiskDetail] = useState(null);
  const [riskLoading, setRiskLoading] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (isSuperAdmin && selectedOrg) params.organization_id = selectedOrg;
      if (selectedUser) params.user_id = selectedUser;
      const { data } = await axios.get(`${API}/usage/summary`, { headers: getHeaders(), params });
      setSummary(data);
    } catch {
      setSummary(null);
    }
    setLoading(false);
  }, [startDate, endDate, selectedOrg, selectedUser, isSuperAdmin]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  // When org changes (super admin), clear the selected user so the user
  // dropdown shows users for the newly selected org only. The fetch itself is
  // triggered by the effect above (fetchSummary is re-created whenever
  // selectedOrg changes), so we only need to reset the user here.
  useEffect(() => {
    setSelectedUser('');
  }, [selectedOrg]);
  // Sort risk breakdown once per summary change — this was previously computed
  // on every render inside JSX (map/sort/entries), causing a hot path when the
  // summary card re-renders on filter changes.
  const sortedRiskBreakdown = useMemo(() => {
    if (!summary?.risk_breakdown) return [];
    const order = ['critical', 'high', 'medium', 'low', 'clear'];
    return Object.entries(summary.risk_breakdown).sort(([a], [b]) => order.indexOf(a) - order.indexOf(b));
  }, [summary]);

  const handleExport = async () => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (isSuperAdmin && selectedOrg) params.organization_id = selectedOrg;
      if (selectedUser) params.user_id = selectedUser;
      const { data } = await axios.get(`${API}/usage/export`, {
        headers: getHeaders(), params, responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `myshield_usage_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
    } catch (e) { console.error('Export failed', e); }
  };

  const fetchRiskDetails = async (level) => {
    setRiskLoading(true);
    setRiskDetail({ level, records: [], total: 0 });
    try {
      const params = { risk_level: level };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (isSuperAdmin && selectedOrg) params.organization_id = selectedOrg;
      if (selectedUser) params.user_id = selectedUser;
      const { data } = await axios.get(`${API}/usage/risk-details`, { headers: getHeaders(), params });
      setRiskDetail(data);
    } catch {
      setRiskDetail(null);
    }
    setRiskLoading(false);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="usage-export-page">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Usage & Billing</h1>
            <p className="text-sm text-slate-500 mt-1">
              {isSuperAdmin ? 'System-wide screening usage — filter by organization for billing' : 'Your organization\'s screening usage'}
            </p>
          </div>
          <Button onClick={handleExport} data-testid="export-usage-btn">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>

        {/* Filters */}
        <Card className="border border-slate-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex flex-col sm:flex-row items-end gap-4 flex-wrap">
              {/* Organization filter — Super Admin only */}
              {isSuperAdmin && summary?.organizations?.length > 0 && (
                <div className="w-full sm:w-auto">
                  <label className="text-sm font-medium text-slate-700 block mb-1">
                    <Building2 className="w-3.5 h-3.5 inline mr-1" />Organization
                  </label>
                  <select
                    value={selectedOrg}
                    onChange={(e) => setSelectedOrg(e.target.value)}
                    className="w-full sm:w-56 h-10 rounded-md border border-slate-300 px-3 text-sm bg-white"
                    data-testid="org-filter-select"
                  >
                    <option value="">All Organizations</option>
                    {summary.organizations.map((org) => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {/* User filter — available to org admin (own org) and super admin (selected org) */}
              {summary?.users?.length > 0 && (
                <div className="w-full sm:w-auto">
                  <label className="text-sm font-medium text-slate-700 block mb-1">
                    <User className="w-3.5 h-3.5 inline mr-1" />User
                  </label>
                  <select
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                    className="w-full sm:w-56 h-10 rounded-md border border-slate-300 px-3 text-sm bg-white"
                    data-testid="user-filter-select"
                  >
                    <option value="">All Users</option>
                    {summary.users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.full_name ? `${u.full_name} (${u.email})` : u.email}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Start Date</label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="usage-start-date" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">End Date</label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="usage-end-date" />
              </div>
              <Button onClick={fetchSummary} variant="outline" data-testid="usage-filter-btn">Apply Filter</Button>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading usage data...</div>
        ) : summary ? (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="p-5 border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg"><BarChart3 className="w-5 h-5 text-blue-700" /></div>
                  <div>
                    <p className="text-sm text-slate-500">Total Screenings</p>
                    <p className="text-2xl font-bold text-slate-900" data-testid="total-screenings">{summary.total_screenings?.toLocaleString()}</p>
                  </div>
                </div>
              </Card>
              <Card className="p-5 border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-violet-100 rounded-lg"><FileSpreadsheet className="w-5 h-5 text-violet-700" /></div>
                  <div>
                    <p className="text-sm text-slate-500">API Screenings</p>
                    <p className="text-2xl font-bold text-slate-900">{summary.api_screenings?.toLocaleString()}</p>
                  </div>
                </div>
              </Card>
              <Card className="p-5 border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg"><Shield className="w-5 h-5 text-emerald-700" /></div>
                  <div>
                    <p className="text-sm text-slate-500">UI Scans</p>
                    <p className="text-2xl font-bold text-slate-900">{summary.ui_scans?.toLocaleString()}</p>
                  </div>
                </div>
              </Card>
              <Card className="p-5 border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg"><TrendingUp className="w-5 h-5 text-amber-700" /></div>
                  <div>
                    <p className="text-sm text-slate-500">Credits Used</p>
                    <p className="text-2xl font-bold text-slate-900">{summary.total_credits_used?.toLocaleString()}</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Risk Breakdown — Clickable */}
            {summary.risk_breakdown && Object.keys(summary.risk_breakdown).length > 0 && (
              <Card className="border border-slate-200">
                <CardContent className="pt-5">
                  <h3 className="font-semibold text-slate-900 mb-4">Risk Level Breakdown</h3>
                  <p className="text-xs text-slate-500 mb-3">Click a risk level to view individual screening records</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    {sortedRiskBreakdown
                      .map(([level, count]) => {
                        const style = getRiskStyle(level);
                        const isSelected = riskDetail?.risk_level === level;
                        return (
                          <button
                            key={level}
                            onClick={() => fetchRiskDetails(level)}
                            className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${style.bg} ${isSelected ? 'ring-2 ring-offset-1 ring-slate-400 shadow-md' : ''}`}
                            data-testid={`risk-level-${level}`}
                          >
                            {level === 'critical' && <AlertTriangle className={`w-5 h-5 ${style.icon}`} />}
                            {level === 'high' && <AlertTriangle className={`w-5 h-5 ${style.icon}`} />}
                            {level === 'medium' && <Shield className={`w-5 h-5 ${style.icon}`} />}
                            {level === 'low' && <CheckCircle2 className={`w-5 h-5 ${style.icon}`} />}
                            {level === 'clear' && <CheckCircle2 className={`w-5 h-5 ${style.icon}`} />}
                            {!['critical','high','medium','low','clear'].includes(level) && <Shield className={`w-5 h-5 ${style.icon}`} />}
                            <div className="text-left">
                              <p className="text-xs text-slate-500 capitalize">{level}</p>
                              <p className={`text-lg font-bold ${style.text}`}>{count.toLocaleString()}</p>
                            </div>
                            <ChevronRight className="w-4 h-4 ml-auto text-slate-400" />
                          </button>
                        );
                      })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Risk Detail Panel */}
            {riskDetail && (
              <Card className="border border-slate-200" data-testid="risk-detail-panel">
                <CardContent className="pt-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900">
                        <span className="capitalize">{riskDetail.risk_level}</span> Risk Screenings
                      </h3>
                      <Badge className={getRiskStyle(riskDetail.risk_level).badge}>
                        {riskDetail.total} records
                      </Badge>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setRiskDetail(null)} data-testid="close-risk-detail">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>

                  {riskLoading ? (
                    <div className="flex items-center justify-center py-8 text-slate-500">
                      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading records...
                    </div>
                  ) : riskDetail.records?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200 text-left">
                            <th className="px-3 py-2 font-medium text-slate-600">Name</th>
                            <th className="px-3 py-2 font-medium text-slate-600">Risk Score</th>
                            <th className="px-3 py-2 font-medium text-slate-600">Status</th>
                            <th className="px-3 py-2 font-medium text-slate-600">Source</th>
                            <th className="px-3 py-2 font-medium text-slate-600">Matches</th>
                            <th className="px-3 py-2 font-medium text-slate-600">Date</th>
                            <th className="px-3 py-2 font-medium text-slate-600">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {riskDetail.records.map((rec, idx) => (
                            <tr key={rec.id || idx} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="px-3 py-2.5 font-medium text-slate-900">{rec.screened_name || '-'}</td>
                              <td className="px-3 py-2.5">
                                <span className={`font-semibold ${getRiskStyle(riskDetail.risk_level).text}`}>
                                  {rec.risk_score ? `${(rec.risk_score * 100).toFixed(0)}%` : '-'}
                                </span>
                              </td>
                              <td className="px-3 py-2.5">
                                <Badge variant={rec.status === 'blocked' ? 'destructive' : 'secondary'} className="text-xs">
                                  {rec.status || '-'}
                                </Badge>
                              </td>
                              <td className="px-3 py-2.5">
                                <Badge variant="outline" className="text-xs">{rec.source}</Badge>
                              </td>
                              <td className="px-3 py-2.5 text-slate-600">{rec.total_matches ?? '-'}</td>
                              <td className="px-3 py-2.5 text-slate-500 text-xs whitespace-nowrap">
                                {rec.screened_at ? new Date(rec.screened_at).toLocaleString() : '-'}
                              </td>
                              <td className="px-3 py-2.5 text-slate-500 text-xs">{rec.recommendation || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-center py-6 text-slate-500">No records found for this risk level</p>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <div className="text-center py-12 text-slate-500">No usage data available</div>
        )}
      </div>
    </Layout>
  );
}
